// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { getCookie } from "@insite/client-framework/Common/Cookies";
import { emptyGuid } from "@insite/client-framework/Common/StringHelpers";
import logger from "@insite/client-framework/Logger";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setSignInOverlayIsOpen from "@insite/client-framework/Store/Components/SignInOverlay/Handlers/SetSignInOverlayIsOpen";
import { getCurrentUserIsGuest, getIsPunchOutSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import cancelPunchOut from "@insite/client-framework/Store/Context/Handlers/CancelPunchOut";
import setShowInventory from "@insite/client-framework/Store/Context/Handlers/SetShowInventory";
import setShowPricing from "@insite/client-framework/Store/Context/Handlers/SetShowPricing";
import signOut from "@insite/client-framework/Store/Context/Handlers/SignOut";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadBillTo from "@insite/client-framework/Store/Data/BillTos/Handlers/LoadBillTo";
import { getCategoryState } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import loadCategory from "@insite/client-framework/Store/Data/Categories/Handlers/LoadCategory";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import {
    getPageLinkByNodeId,
    getPageLinkByPageType,
    LinkModel,
} from "@insite/client-framework/Store/Links/LinksSelectors";
import setIsReorderConfirmationModalOpen from "@insite/client-framework/Store/Pages/VmiBins/Handlers/SetIsReorderConfirmationModalOpen";
import setNextAction from "@insite/client-framework/Store/Pages/VmiBins/Handlers/SetNextAction";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import { NavigationModeCookieName } from "@insite/content-library/Components/NavigationModeMenu";
import { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { IconPresentationProps } from "@insite/mobius/Icon/Icon";
import Eye from "@insite/mobius/Icons/Eye";
import EyeOff from "@insite/mobius/Icons/EyeOff";
import User from "@insite/mobius/Icons/User";
import { MappedLink } from "@insite/mobius/Menu";
import { TypographyPresentationProps } from "@insite/mobius/Typography/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import {
    trackAsImpersonation,
    trackGaLoginEvent,
    trackGaMyAccountFlyOutEvent,
    trackGaToolbarNavClickEvent,
} from "@stoneage/App/Utilities/DataLayer";
import React, { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";
import { xIsAuthenticated } from "@stoneage/App/Store/Selectors/GeneralSelectors";
import MiniSignIn from "@stoneage/App/Components/MiniSignIn/MiniSignIn";
import FloatingTooltip, { FloatingTooltipStyles } from "@stoneage/App/Components/FloatingTooltip/FloatingTooltip";
import HeaderClickable, { HeaderClickableStyles } from "@stoneage/App/Components/Header/HeaderClickable";
import MiniMyAccount from "@stoneage/App/Components/MiniMyAccount/MiniMyAccount";
import { CustomAppState } from "@stoneage/App/Store/Reducers";

const enum fields {
    visibilityState = "visibilityState",
    icon = "icon",
    includeAccountMenu = "includeAccountMenu",
    showPricingToggle = "showPricingToggle",
    showInventoryToggle = "showInventoryToggle",
    // XNG-Changes
    links = "links",
}

const mapStateToProps = (state: CustomAppState) => ({
    isSignInPage: getCurrentPage(state).type === "SignInPage",
    userName: state.context.session.userName,
    myAccountPageLink: getPageLinkByPageType(state, "MyAccountPage"),
    signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
    currentUserIsGuest: getCurrentUserIsGuest(state),
    isPunchOutSession: getIsPunchOutSession(state),
    currentLocation: getLocation(state),
    displayChangeCustomerLink: state.context.session?.displayChangeCustomerLink || false,
    displayPricingAndInventory: state.context.session?.displayPricingAndInventory || false,
    showInventory: state.context.showInventory,
    showPricing: state.context.showPricing,
    isReorderConfirmationModalOpen: state.pages.vmiBins.isReorderConfirmationModalOpen,
    hasChangeLastQty:
        state.pages.vmiBins.selectVmiBinsChangedQty &&
        Object.keys(state.pages.vmiBins.selectVmiBinsChangedQty).length > 0,
    // XNG-Changes
    isTabletScreen: state.xCommon.isTabletScreen,
    isAuthenticated: xIsAuthenticated(state),
    selectedBillToId: state.context.session.billToId,
    billToState: getBillToState(state, state.context.session.billToId),
    impersonation: trackAsImpersonation(state),
});

const mapDispatchToProps = {
    signOut,
    cancelPunchOut,
    setSignInOverlayIsOpen,
    setShowInventory,
    setShowPricing,
    setIsReorderConfirmationModalOpen,
    setNextAction,
    // XNG-Changes
    loadBillTo,
};

export interface HeaderSignInStyles {
    signOutWrapper?: InjectableCss;
    titleClickable?: ClickablePresentationProps;
    titleIcon?: IconPresentationProps;
    titleTypography?: TypographyPresentationProps;
    signOutClickable?: ClickablePresentationProps;
    // XNG-Changes
    headerClickable?: HeaderClickableStyles;
    myAccountTooltip?: FloatingTooltipStyles;
    signInTooltip?: FloatingTooltipStyles;
}

export const headerSignInStyles: HeaderSignInStyles = {
    signOutWrapper: {
        css: css`
            display: inline-flex;
            padding: 6px 0;
        `,
    },
    titleClickable: {
        css: css`
            margin-left: 10px;
        `,
    },
    titleIcon: {
        size: 22,
    },
    titleTypography: {
        css: css`
            margin-left: 10px;
            max-width: 150px;
        `,
        ellipsis: true,
        transform: "uppercase",
    },
    signOutClickable: {
        css: css`
            flex: 0 0 auto;
            margin-left: 16px;
            text-transform: uppercase;
        `,
    },
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.visibilityState]: string;
        [fields.icon]: string;
        [fields.includeAccountMenu]: boolean;
        [fields.showPricingToggle]: boolean;
        [fields.showInventoryToggle]: boolean;
        // XNG-Changes
        [fields.links]: LinkModel[];
    };
}

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasHistory &
    ThemeProps<BaseTheme>;

let icon: React.ComponentType<IconPresentationProps>;

export const HeaderSignIn = ({
    userName,
    isSignInPage,
    currentUserIsGuest,
    signOut,
    cancelPunchOut,
    fields,
    history,
    myAccountPageLink,
    signInUrl,
    isPunchOutSession,
    currentLocation,
    displayChangeCustomerLink,
    displayPricingAndInventory,
    showInventory,
    showPricing,
    setSignInOverlayIsOpen,
    setShowInventory,
    setShowPricing,
    theme,
    isReorderConfirmationModalOpen,
    hasChangeLastQty,
    setIsReorderConfirmationModalOpen,
    setNextAction,
    // XNG-Changes
    selectedBillToId,
    billToState,
    loadBillTo,
    isAuthenticated,
    impersonation,
    isTabletScreen,
}: Props) => {
    // XNG-Changes
    const [isMenuOpen, setIsMenuOpen] = React.useState(false);

    const styles = useMergeStyles("headerSignIn", headerSignInStyles);

    useEffect(() => {
        window.onpageshow = function (event) {
            if (event.persisted) {
                window.location.reload();
            }
        };
    }, [userName]);

    // XNG-Changes
    React.useEffect(() => {
        // load current billTo
        if (!billToState.value && !billToState.isLoading && selectedBillToId && isAuthenticated) {
            loadBillTo({ billToId: selectedBillToId });
        }
    }, []);

    // XNG-Changes
    React.useEffect(() => {
        if (billToState.value) {
            // XNG-Change: track GA4 - Because we are not using the Optimizely SignIn, we trigger the ga4 login event here and
            // the event tag will be configured to be caught only once per session
            trackGaLoginEvent(billToState.value.customerNumber, impersonation);
        }
    }, [billToState.value]);

    if (!myAccountPageLink) {
        return null;
    }

    const { visibilityState, includeAccountMenu, showInventoryToggle, showPricingToggle } = fields;
    const showIcon = visibilityState === "both" || visibilityState === "icon";
    const showLabel = visibilityState === "both" || visibilityState === "label";

    if (fields.icon === "User") {
        icon = User;
    }

    const currentMode = getCookie(NavigationModeCookieName);
    let menuItems = (myAccountPageLink?.children?.filter(
        pageLink => !(pageLink.type === "ChangeCustomerPage" && !displayChangeCustomerLink),
    ) ?? []) as MappedLink[];

    const getMenuItem = (title: "Inventory" | "Pricing"): MappedLink => {
        return {
            title: title === "Inventory" ? translate("Inventory") : translate("Pricing"),
            iconProps: {
                src: (title === "Inventory" && showInventory) || (title === "Pricing" && showPricing) ? EyeOff : Eye,
                size: 22,
                css: css`
                    margin-left: 20px;
                    margin-right: -12px;
                `,
            },
            clickableProps: {
                css: css`
                    display: flex;
                    text-transform: uppercase;
                `,
                onClick: () => {
                    title === "Inventory"
                        ? setShowInventory({ showInventory: !showInventory })
                        : setShowPricing({ showPricing: !showPricing });
                },
            },
        };
    };

    if (!currentMode || currentMode === "Storefront") {
        menuItems = menuItems?.filter(pageLink => pageLink.type !== "VmiUsersPage");

        showInventoryToggle && displayPricingAndInventory && menuItems.push(getMenuItem("Inventory"));
        showPricingToggle && displayPricingAndInventory && menuItems.push(getMenuItem("Pricing"));
    }

    const onSignInHandler = (e: React.MouseEvent) => {
        if ((!userName || currentUserIsGuest) && !isSignInPage) {
            e.preventDefault();

            if (theme.signIn?.useOverlay) {
                setSignInOverlayIsOpen({ isOpen: true });
                return;
            }

            // XNG-Change: track GA4
            trackGaToolbarNavClickEvent(translate("Sign In"), impersonation);

            if (!signInUrl) {
                logger.warn("No url was found for SignInPage, defaulting to /SignIn");
                history.push("/MyAccount/SignIn");
            } else {
                const returnUrl = currentLocation.pathname + currentLocation.search;
                if (returnUrl === "/") {
                    history.push(`${signInUrl}`);
                } else {
                    history.push(`${signInUrl}?returnUrl=${encodeURIComponent(returnUrl)}&clientRedirect=true`);
                }
                /**
                 * XNG-Changes
                 * SAINC-799: Fixed the issue with user not being redirected after SSO
                 */
                if (typeof window !== "undefined") {
                    window.localStorage.setItem("returnUrl", `returnUrl=${encodeURIComponent(returnUrl)}`);
                }
            }
        }
    };

    const onSignOutHandler = () => {
        if (!isReorderConfirmationModalOpen && hasChangeLastQty) {
            setIsReorderConfirmationModalOpen({ isReorderConfirmationModalOpen: true });
            setNextAction({ nextAction: signOutFunc });
        } else {
            signOutFunc();
        }
    };

    const onMyAccountHandler = () => {
        if (myAccountPageLink?.url) {
            trackGaMyAccountFlyOutEvent(
                myAccountPageLink.url || "",
                translate("Account"),
                impersonation,
                billToState?.value?.customerNumber,
            );
            history.push(myAccountPageLink?.url as string);
        }
    };

    const signOutFunc = () => {
        isPunchOutSession ? cancelPunchOut() : signOut();
    };

    const priceInventoryToggleHandler = (type: "Price" | "Inventory") => {
        type === "Inventory"
            ? setShowInventory({ showInventory: !showInventory })
            : setShowPricing({ showPricing: !showPricing });
    };

    let signInStatusText = translate("Sign In");
    if (userName && !currentUserIsGuest) {
        signInStatusText = userName;
    }

    if (!isAuthenticated) {
        return (
            <FloatingTooltip
                onOpenChange={open => setIsMenuOpen(open)}
                triggerComponent={
                    <HeaderClickable
                        isOpen={isMenuOpen}
                        data-test-selector="signIn"
                        aria-label={translate("Sign In")}
                        extendedStyles={styles.headerClickable}
                        showIcon={showIcon}
                        onClick={onSignInHandler}
                        subTitle={translate("Or Register")}
                    >
                        {showLabel && translate("Sign In")}
                    </HeaderClickable>
                }
                showArrow={false}
                isOpen={isMenuOpen}
                enabled={!(isSignInPage || isTabletScreen)}
                offset={{ mainAxis: 0 }}
                placement="bottom-end"
                extendedStyles={styles.signInTooltip}
            >
                <MiniSignIn />
            </FloatingTooltip>
        );
    }

    return (
        <FloatingTooltip
            onOpenChange={open => setIsMenuOpen(open)}
            triggerComponent={
                <HeaderClickable
                    isOpen={isMenuOpen}
                    data-test-selector="myAccountManagement"
                    aria-label={translate("My Account")}
                    extendedStyles={styles.headerClickable}
                    showIcon={showIcon}
                    onClick={onMyAccountHandler}
                    subTitle={translate("My Account")}
                >
                    {showLabel && translate("Welcome")}
                </HeaderClickable>
            }
            showArrow={false}
            isOpen={isMenuOpen}
            enabled={!isSignInPage}
            offset={{ mainAxis: 0 }}
            placement="bottom-end"
            extendedStyles={styles.myAccountTooltip}
        >
            <MiniMyAccount
                linksField={fields.links}
                displayChangeCustomerLink={displayChangeCustomerLink}
                onSignOutHandler={onSignOutHandler}
                showInventory={showInventory}
                showInventoryToggle={showInventoryToggle}
                showPricingToggle={showPricingToggle}
                showPricing={showPricing}
                displayPricingAndInventory={displayPricingAndInventory}
                priceInventoryToggleHandler={priceInventoryToggleHandler}
                isPunchOutSession={isPunchOutSession}
            />
        </FloatingTooltip>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(withTheme(HeaderSignIn))),
    definition: {
        displayName: "Header Sign In",
        icon: "arrow-right-to-bracket",
        fieldDefinitions: [
            {
                name: fields.visibilityState,
                displayName: "Settings",
                editorTemplate: "RadioButtonsField",
                options: [
                    {
                        displayName: "Both",
                        value: "both",
                    },
                    {
                        displayName: "Show Label",
                        value: "label",
                    },
                    {
                        displayName: "Show Icon",
                        value: "icon",
                    },
                ],
                defaultValue: "both",
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.icon,
                displayName: "SignIn Icon",
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "User",
                        value: "User",
                    },
                ],
                defaultValue: "User",
                fieldType: "General",
                sortOrder: 2,
            },
            {
                name: fields.includeAccountMenu,
                displayName: "Include My Account Menu",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 3,
            },
            {
                name: fields.showPricingToggle,
                displayName: "Show/Hide Pricing Toggle",
                editorTemplate: "CheckboxField",
                isVisible: widget => widget.fields.includeAccountMenu,
                defaultValue: false,
                fieldType: "General",
                sortOrder: 4,
            },
            {
                name: fields.showInventoryToggle,
                displayName: "Show/Hide Inventory Toggle",
                editorTemplate: "CheckboxField",
                isVisible: widget => widget.fields.includeAccountMenu,
                defaultValue: false,
                fieldType: "General",
                sortOrder: 5,
            },
            // XNG-Changes,
            {
                name: fields.links,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields, state) => {
                    const { type, value } = item.fields.destination;
                    const { overriddenTitle, title } = item.fields;
                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return overriddenTitle || link?.title;
                    }
                    if (type === "Url") {
                        return overriddenTitle ?? title ?? value;
                    }
                    return value;
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isRequired: true,
                        allowCategories: item => false,
                    },
                    {
                        name: "overriddenTitle",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: false,
                    },
                    {
                        name: "openInNewTab",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                ],
            },
        ],
        group: "Common",
    },
};

export default widgetModule;
